import posthog from "posthog-js";
import { axiosBasic, getUserInfo } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { actionTypes } from "./projectActionTypes";
import { UPDATE_PAGING } from "../../common/pagination/paginationActionTypes";
import { Common } from "../../../utils";
import {
  buildProjectNotes,
  WORKFLOW_STATUS,
  workflowStatusToApiStatus,
} from "../../common/header/settings/projects/projectFormMeta";

const { Project } = config.REST_API;

/**
 * Team ids may be legacy integers or GUID strings from the API.
 * Returns a finite number when the value is purely numeric, otherwise a trimmed string (e.g. UUID).
 */
export function normalizeTeamIdForPayload(tid) {
  if (tid == null || tid === "") return null;
  if (typeof tid === "number" && Number.isFinite(tid)) return tid;
  const s = String(tid).trim();
  if (!s) return null;
  const n = Number(s);
  if (Number.isFinite(n) && s === String(n)) return n;
  return s;
}

/** Deduped list of team ids (legacy single + TeamIds); all string for API JSON. */
export function normalizeTeamIdsList(model) {
  if (!model || typeof model !== "object") return [];
  const out = [];
  const seen = new Set();
  const push = (raw) => {
    const n = normalizeTeamIdForPayload(raw);
    if (n == null) return;
    const key = String(n);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(key);
  };
  if (Array.isArray(model.teamIds)) {
    model.teamIds.forEach(push);
  }
  push(model.teamId);
  return out;
}

/** App user ids for notes/meta: numeric ids as numbers, GUIDs as strings (matches form state). */
function normalizeUserIdForMeta(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  if (!s) return null;
  const n = Number(s);
  if (Number.isFinite(n) && s === String(n)) return n;
  return s;
}

const getAll = (items) => ({ type: actionTypes.PR_SHOW_LIST, payload: items });
const appendAll = (items) => ({ type: actionTypes.PR_APPEND_LIST, payload: items });
const softDelete = (item) => ({ type: actionTypes.PR_DELETE_SOFT, payload: item });

/** @param listingOptions.append When true, merges into existing list and uses a lighter loading state. */
export const ProjectListing = (options, currentPage, listingOptions = {}) => {
  const { append = false } = listingOptions;
  const take = options.take ?? options.limit ?? 10;
  const newUrl =
    Project.Get +
    options.start +
    "/" +
    options.page +
    "/" +
    take;
  return (dispatch) => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { start: options.start, page: options.page, take, currentPage, append };
    if (append) {
      dispatch({ type: actionTypes.PR_LOADING_MORE_START });
    } else {
      dispatch({ type: actionTypes.PR_LOADING_START });
    }
    return axiosBasic
      .get(newUrl)
      .then((response) => {
        const baseModel = response.data;
        const success = baseModel.success;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("ProjectListing", {
            type: success ? "info" : "exception",
            userId,
            success,
            userInput,
            reason: success ? undefined : baseModel.message,
          });
        }
        if (success) {
          dispatch({
            type: UPDATE_PAGING,
            payload: {
              data: { total: baseModel.total, currentPage, pageLimit: take },
            },
          });
          dispatch(append ? appendAll(baseModel.data) : getAll(baseModel.data));
        } else {
          if (append) dispatch({ type: actionTypes.PR_LOADING_MORE_STOP });
          else dispatch({ type: actionTypes.PR_LOADING_STOP });
          Common.showErrorMessage(baseModel.message);
        }
        return baseModel;
      })
      .catch((error) => {
        if (append) dispatch({ type: actionTypes.PR_LOADING_MORE_STOP });
        else dispatch({ type: actionTypes.PR_LOADING_STOP });
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("ProjectListing", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          });
        }
        Common.showErrorMessage("Something went wrong");
        console.log(error);
        throw error;
      });
  };
};

/**
 * GET api/v1.0/Project/{id} — full project for edit form (not the paged list).
 * Returns `{ success, data, message }` with `data` as the API project DTO.
 */
export async function fetchProjectById(id) {
  const sid = String(id ?? "").trim();
  if (!sid) {
    return { success: false, message: "Missing project id", data: null };
  }
  try {
    const response = await axiosBasic.get(`${Project.Get}${encodeURIComponent(sid)}`);
    const body = response.data;
    /** Unwrap common API envelope shapes (camelCase / PascalCase / nested). */
    const unwrap = (b) => {
      if (!b || typeof b !== "object") return null;
      const direct = [
        b.project,
        b.Project,
        b.data,
        b.Data,
        b.result,
        b.Result,
        b.value,
        b.Value,
      ];
      for (const c of direct) {
        if (c == null) continue;
        if (Array.isArray(c) && c.length === 1 && c[0] && typeof c[0] === "object") return c[0];
        if (typeof c === "object" && !Array.isArray(c)) return c;
      }
      const hasId =
        typeof b.id === "string" ||
        typeof b.id === "number" ||
        typeof b.Id === "string" ||
        typeof b.Id === "number";
      if (hasId || b.title != null || b.Title != null || b.name != null || b.Name != null) {
        return b;
      }
      return null;
    };
    /** `data: { project: { ... } }` is common — first unwrap picks the wrapper; peel `project` off. */
    const peelProject = (raw) => {
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
      const inner = raw.project ?? raw.Project;
      if (inner && typeof inner === "object" && !Array.isArray(inner)) {
        return inner;
      }
      return raw;
    };
    const rawUnwrapped = unwrap(body);
    const peeled = peelProject(rawUnwrapped);
    /** GET often returns `project` plus siblings on the same envelope: `projectTeamAssignments`, `teams`, `userTeams`. */
    const ENVELOPE_TEAM_KEYS = [
      "projectTeamAssignments",
      "ProjectTeamAssignments",
      "teams",
      "Teams",
      "userTeams",
      "UserTeams",
    ];
    const data =
      peeled && typeof peeled === "object" && !Array.isArray(peeled)
        ? { ...peeled }
        : peeled;
    if (data && typeof data === "object" && !Array.isArray(data) && rawUnwrapped && typeof rawUnwrapped === "object" && !Array.isArray(rawUnwrapped)) {
      for (const k of ENVELOPE_TEAM_KEYS) {
        if (rawUnwrapped[k] != null) data[k] = rawUnwrapped[k];
      }
    }
    const explicitFail = body?.success === false || body?.Success === false;
    const success = !explicitFail && data != null && typeof data === "object";
    if (!success) {
      return {
        success: false,
        message: body?.message ?? body?.Message ?? "Request failed",
        data: null,
      };
    }
    return { success: true, data, message: body?.message ?? body?.Message };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.message ?? error?.message ?? "Request failed",
      data: null,
    };
  }
}

function metaPayloadFromModel(model) {
  const mode = model.initialTeamMode ?? "none";
  const memberIds = Array.isArray(model.teamUserIds)
    ? model.teamUserIds.map((x) => normalizeUserIdForMeta(x)).filter((x) => x != null)
    : [];
  const newTeamMemberStrIds = Array.isArray(model.newTeamMemberUserIds)
    ? model.newTeamMemberUserIds.map((x) => String(x).trim()).filter((s) => s !== "")
    : [];
  const meta = {
    priority: model.priority,
    departmentId: model.departmentId,
    clientName: (model.clientName || "").trim(),
    technology: (model.technology || "").trim(),
    description: (model.description || "").trim(),
    workflowStatus: workflowStatusToApiStatus(model.workflowStatus),
    startDate: model.startDate || null,
    endDate: model.endDate || null,
    supervisorUserId: model.supervisorUserId ?? null,
    teamUserIds: memberIds,
    newTeamMemberUserIds: newTeamMemberStrIds,
    initialTeamMode: mode,
    isInternal: !!model.isInternal,
  };
  if (mode === "existing" || mode === "new") {
    const teamList = normalizeTeamIdsList(model);
    meta.teamIds = teamList;
  } else if (mode === "none") {
    meta.teamIds = [];
  }
  if (mode === "new") {
    const nn = (model.newTeamName || "").trim();
    if (nn) meta.newTeamName = nn;
  }
  /** Single object for backend parsers: linked team + chosen initial member user ids */
  const pt = {
    initialTeamMode: mode,
    initialMemberUserIds: memberIds,
    newTeamMemberUserIds: newTeamMemberStrIds,
  };
  if (mode === "existing" || mode === "new") {
    pt.teamIds = normalizeTeamIdsList(model);
  } else if (mode === "none") {
    pt.teamIds = [];
  }
  if (mode === "none") {
    pt.existingTeamSlots = [];
  } else if (mode === "existing" && Array.isArray(model.existingTeamSlots)) {
    pt.existingTeamSlots = model.existingTeamSlots;
  }
  if (mode === "new") {
    const nn = (model.newTeamName || "").trim();
    if (nn) pt.newTeamName = nn;
  }
  meta.projectInitialTeam = pt;
  return meta;
}

/**
 * POST create / PUT update: `InitialTeamMode` existing — one entry per linked team with optional members to add to that team.
 */
export function buildExistingTeamsPayload(model) {
  const slots = model.existingTeamSlots;
  if (!Array.isArray(slots)) return [];
  const out = [];
  for (const slot of slots) {
    if (!slot || slot.teamId == null || slot.teamId === "") continue;
    const tid = normalizeTeamIdForPayload(slot.teamId);
    if (tid == null) continue;
    const memberUserIds = (slot.additionalMemberUserIds || [])
      .map((x) => String(x).trim())
      .filter((s) => s !== "");
    out.push({
      TeamId: String(tid),
      MemberUserIds: memberUserIds,
    });
  }
  return out;
}

/** Maps UI model (after team resolution) to API ProjectCreateRequest. */
export function buildProjectCreateRequest(model) {
  const mode = model.initialTeamMode ?? "none";
  const today = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const todayYmd = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const startYmd =
    model.startDate && String(model.startDate).length >= 10
      ? String(model.startDate).slice(0, 10)
      : todayYmd;
  let endYmd =
    model.endDate && String(model.endDate).length >= 10
      ? String(model.endDate).slice(0, 10)
      : startYmd;
  if (endYmd < startYmd) endYmd = startYmd;

  const priorityMap = { low: 0, medium: 1, high: 2, critical: 3 };
  const pr = model.priority;
  const priorityVal = priorityMap[pr] !== undefined ? priorityMap[pr] : 1;

  const status = workflowStatusToApiStatus(model.workflowStatus);

  const supervisorId = String(
    model.supervisorUserId !== "" && model.supervisorUserId != null ? model.supervisorUserId : ""
  );

  let teamIdsList = [];
  if (mode === "new") {
    teamIdsList = normalizeTeamIdsList(model);
  }

  let teamLeadIdForNew = null;
  if (mode === "new") {
    if (supervisorId) {
      teamLeadIdForNew = supervisorId;
    } else if (Array.isArray(model.teamUserIds) && model.teamUserIds.length > 0) {
      teamLeadIdForNew = String(model.teamUserIds[0]);
    }
  }

  const newMemberIdsRaw = Array.isArray(model.newTeamMemberUserIds)
    ? model.newTeamMemberUserIds.map((x) => String(x).trim()).filter((s) => s !== "")
    : [];
  const leadStr = teamLeadIdForNew ? String(teamLeadIdForNew) : "";
  const newTeamMemberUserIdsForApi =
    mode === "new" ? newMemberIdsRaw.filter((id) => id !== leadStr) : [];

  const existingTeamsPayload = mode === "existing" ? buildExistingTeamsPayload(model) : null;

  /** PascalCase keys match ProjectCreateRequest for JSON serializers that are case-sensitive. */
  return {
    Name: (model.name || "").trim(),
    Priority: priorityVal,
    DepartmentId:
      model.departmentId === "" || model.departmentId == null ? null : Number(model.departmentId),
    Status: status,
    ClientName: (model.clientName || "").trim(),
    Technology: (model.technology || "").trim(),
    Purpose: (model.purpose || "").trim(),
    Description: (model.description || "").trim(),
    StartDate: `${startYmd}T00:00:00`,
    ExpectedEndDate: `${endYmd}T23:59:59`,
    SupervisorId: supervisorId,
    InitialTeamMode: mode,
    TeamIds: mode === "existing" ? [] : teamIdsList,
    TeamLeadId: mode === "new" ? teamLeadIdForNew : null,
    NewTeamName: mode === "new" ? (model.newTeamName || "").trim() || null : null,
    IsInternal: !!model.isInternal,
    NewTeamMemberUserIds: newTeamMemberUserIdsForApi,
    ExistingTeams: existingTeamsPayload,
  };
}

export const saveProject = (model) => {
  const body = buildProjectCreateRequest(model);
  return (dispatch) => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { hasName: !!body.Name, mode: body.InitialTeamMode };
    dispatch({ type: actionTypes.PR_LOADING_START });
    return axiosBasic
      .post(Project.CreateProject, body)
      .then((response) => {
        const res = response.data;
        const success = res.success;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("saveProject", {
            type: success ? "info" : "exception",
            userId,
            success,
            userInput,
            reason: success ? undefined : res.message,
          });
        }
        if (success) {
          dispatch({ type: actionTypes.PR_ADD_SUCCESS, payload: res.data });
        }
        dispatch({ type: actionTypes.PR_LOADING_STOP });
        return res;
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("saveProject", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          });
        }
        dispatch({ type: actionTypes.PR_LOADING_STOP });
        const payload = error?.response?.data;
        if (payload && typeof payload === "object") {
          return { success: false, message: payload.message ?? payload.Message ?? "Request failed" };
        }
        return { success: false, message: error?.message ?? "Request failed" };
      });
  };
};

const toPatchBody = (item) => {
  const id = item.id ?? item.Id;
  const name = item.name ?? item.Name;
  const isActive = item.isActive !== undefined ? item.isActive : item.IsActive !== false;
  let notes = item.notes ?? item.Notes ?? "";
  if (item.purpose !== undefined) {
    notes = buildProjectNotes((item.purpose || "").trim(), metaPayloadFromModel(item));
  }
  const isInternal =
    item.isInternal !== undefined
      ? !!item.isInternal
      : item.IsInternal !== undefined
        ? !!item.IsInternal
        : undefined;
  const mode = item.initialTeamMode ?? "none";

  const today = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const todayYmd = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const startYmd =
    item.startDate && String(item.startDate).length >= 10
      ? String(item.startDate).slice(0, 10)
      : todayYmd;
  let endYmd =
    item.endDate && String(item.endDate).length >= 10 ? String(item.endDate).slice(0, 10) : startYmd;
  if (endYmd < startYmd) endYmd = startYmd;

  const priorityMap = { low: 0, medium: 1, high: 2, critical: 3 };
  const pr = item.priority;
  const priorityVal = priorityMap[pr] !== undefined ? priorityMap[pr] : 1;
  const status = workflowStatusToApiStatus(item.workflowStatus);
  const supervisorId = String(
    item.supervisorUserId !== "" && item.supervisorUserId != null ? item.supervisorUserId : ""
  );

  const body = {
    Id: id,
    Name: name,
    Notes: notes,
    IsActive: isActive,
    Priority: priorityVal,
    DepartmentId:
      item.departmentId === "" || item.departmentId == null ? null : Number(item.departmentId),
    Status: status,
    ClientName: (item.clientName || "").trim(),
    Technology: (item.technology || "").trim(),
    Purpose: (item.purpose || "").trim(),
    Description: (item.description || "").trim(),
    StartDate: `${startYmd}T00:00:00`,
    ExpectedEndDate: `${endYmd}T23:59:59`,
    SupervisorId: supervisorId,
  };
  if (isInternal !== undefined) {
    body.IsInternal = isInternal;
  }
  if (item.teamIds !== undefined) {
    body.TeamIds = Array.isArray(item.teamIds) ? item.teamIds.map((x) => String(x).trim()).filter(Boolean) : [];
  }
  if (item.initialTeamMode !== undefined && item.initialTeamMode !== null) {
    body.InitialTeamMode = item.initialTeamMode;
  }
  if (mode === "existing" && Array.isArray(item.existingTeamSlots)) {
    body.ExistingTeams = buildExistingTeamsPayload(item);
  } else if (mode === "none") {
    body.ExistingTeams = [];
  }
  let newTeamLeadForPatch = "";
  if (mode === "new") {
    if (
      item.supervisorUserId !== undefined &&
      item.supervisorUserId !== null &&
      item.supervisorUserId !== ""
    ) {
      newTeamLeadForPatch = String(item.supervisorUserId);
    } else if (Array.isArray(item.teamUserIds) && item.teamUserIds.length > 0) {
      newTeamLeadForPatch = String(item.teamUserIds[0]);
    }
    if (newTeamLeadForPatch) body.TeamLeadId = newTeamLeadForPatch;
    const nn = (item.newTeamName || "").trim();
    if (nn) body.NewTeamName = nn;
  }
  if (item.newTeamMemberUserIds !== undefined) {
    let raw = Array.isArray(item.newTeamMemberUserIds)
      ? item.newTeamMemberUserIds.map((x) => String(x).trim()).filter((s) => s !== "")
      : [];
    if (mode === "new" && newTeamLeadForPatch) {
      raw = raw.filter((id) => id !== newTeamLeadForPatch);
    }
    body.NewTeamMemberUserIds = raw;
  }
  return body;
};

/** Body for PUT UpdateProject/{id} — id is sent in the route, not duplicated in JSON. */
const toProjectUpdateRequestBody = (patchBody) => {
  const { Id: _id, ...rest } = patchBody;
  return rest;
};

export const patchProject = (item) => {
  const obj = toPatchBody(item);
  const id = obj.Id;
  const body = toProjectUpdateRequestBody(obj);
  return (dispatch) => {
    if (id == null || id === "") {
      dispatch({ type: actionTypes.PR_LOADING_STOP });
      return Promise.resolve({ success: false, message: "Missing project id" });
    }
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { id, hasName: !!body.Name, hasNotes: !!body.Notes };
    dispatch({ type: actionTypes.PR_LOADING_START });
    return axiosBasic
      .put(`${Project.UpdateProject}${encodeURIComponent(String(id))}`, body)
      .then((response) => {
        const res = response.data;
        const success = res.success;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("patchProject", {
            type: success ? "info" : "exception",
            userId,
            success,
            userInput,
            reason: success ? undefined : res.message,
          });
        }
        if (success) {
          dispatch({ type: actionTypes.PR_UPDATE_SUCCESS, payload: res.data });
        }
        dispatch({ type: actionTypes.PR_LOADING_STOP });
        return res;
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("patchProject", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          });
        }
        dispatch({ type: actionTypes.PR_LOADING_STOP });
        return error;
      });
  };
};

export const deleteProject = (item) => {
  const id = item.id ?? item.Id;
  return (dispatch) => {
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = { id };
    dispatch({ type: actionTypes.PR_LOADING_START });
    return axiosBasic
      .delete(`${Project.Delete}${encodeURIComponent(id ?? "")}`, {
        transformRequest: [
          (data, headers) => {
            delete headers["Content-Type"];
            return data;
          },
        ],
      })
      .then((response) => {
        dispatch({ type: actionTypes.PR_LOADING_STOP });
        const baseModel = response.data;
        const success = baseModel.success;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("deleteProject", {
            type: success ? "info" : "exception",
            userId,
            success,
            userInput,
            reason: success ? undefined : baseModel.message,
          });
        }
        if (success) {
          const list = baseModel.data ?? baseModel.Data;
          if (Array.isArray(list)) {
            dispatch(getAll(list));
          } else {
            dispatch(softDelete(item));
          }
        }
        const msg =
          typeof baseModel.message === "string"
            ? baseModel.message
            : baseModel.message?.message ?? baseModel.message?.Message;
        return { success: !!success, message: msg };
      })
      .catch((error) => {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("deleteProject", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          });
        }
        dispatch({ type: actionTypes.PR_LOADING_STOP });
        throw error;
      });
  };
};
